import React, { useEffect, useState, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { AnimatedSection } from '../AnimatedSection';
import { ReviewsGrid } from '../Reviews/Reviews';

const ContentCreator = () => {
  // Scroll to Pricing section
  const scrollToPricing = () => {
    const pricingSection = document.querySelector('[class*="py-16 px-4 md:py-20 bg-[#f4f1e9]"]');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Scroll to How it works section
  const scrollToHowItWorks = () => {
    const howItWorksSection = document.getElementById("how-it-works");
    if (howItWorksSection) {
      howItWorksSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Embla Carousel for videos
  const videoSources = [
    '/assets/content-creator/chasingmyluc-wedding-recap.webm',
    '/assets/content-creator/pov-you-were-born-with-built-in-bridesmaids.webm',
    '/assets/content-creator/mataya-volleyball-street-style-interviews.webm',
    '/assets/content-creator/the-listers-recap-720p-1.webm',
    '/assets/content-creator/fun-wedding-edit-wedding-content-creation-virginia.webm',
    '/assets/content-creator/chasing-my-luc-dragon-dance.webm',
    '/assets/content-creator/baltimore-wedding-content-creator-M&T-bank.webm',
    '/assets/content-creator/chasingmyluc-wedding-content-creation.webm',
    '/assets/content-creator/memorial-day-interview-date-saver-co.webm',
    '/assets/content-creator/peelable-gummy-ad.webm',
    '/assets/content-creator/ps-church-wedding.webm',
    '/assets/content-creator/the-listers-virginia-wedding-dog-content.webm',
  ];
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  // Ref for the Embla viewport DOM node
  const emblaViewportRef = useRef<HTMLDivElement | null>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [progress, setProgress] = useState<number[]>(Array(videoSources.length).fill(0));

  // Play/pause logic for only one video at a time
  const handlePlayPause = (idx: number) => {
    videoRefs.current.forEach((v, i) => {
      if (v) {
        if (i === idx) {
          if (v.paused) {
            v.play();
            setPlayingIndex(idx);
          } else {
            v.pause();
            setPlayingIndex(null);
          }
        } else {
          v.pause();
        }
      }
    });
  };

  // Update progress bar
  const handleTimeUpdate = (idx: number) => {
    const v = videoRefs.current[idx];
    if (v) {
      setProgress(prev => {
        const updated = [...prev];
        updated[idx] = v.currentTime / v.duration || 0;
        return updated;
      });
    }
  };

  // Seek video
  const handleSeek = (idx: number, percent: number) => {
    const v = videoRefs.current[idx];
    if (v && v.duration) {
      v.currentTime = percent * v.duration;
    }
  };

  // Load thumbnail on metadata load
  const handleLoadedMetadata = (idx: number) => {
    const v = videoRefs.current[idx];
    if (v?.currentTime === 0) {
      v.currentTime = 0.1; // Seek to 0.1s to generate thumbnail
    }
  };

  // Set volume to 10% and pause videos not in view
  useEffect(() => {
    videoRefs.current.forEach((v, idx) => {
      if (v) {
        v.volume = 0.1;
        if (playingIndex !== idx) v.pause();
      }
    });
  }, [playingIndex]);

  // Pause all videos if carousel is out of view
  useEffect(() => {
    const node = emblaViewportRef.current;
    if (!node) return;
    let observer: IntersectionObserver | null = null;
    const handleVisibility = (entries: IntersectionObserverEntry[]) => {
      if (!entries[0].isIntersecting) {
        // Pause all videos if carousel is out of view
        videoRefs.current.forEach(v => v && v.pause());
        setPlayingIndex(null);
      }
    };
    observer = new window.IntersectionObserver(handleVisibility, {
      root: null,
      threshold: 0.01,
    });
    observer.observe(node);
    return () => {
      if (observer && node) observer.unobserve(node);
    };
  }, []);

  // Pause all videos and update selected dot when slide changes
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setPlayingIndex(null);
      videoRefs.current.forEach((v, i) => {
        if (v) v.pause();
      });
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };
    emblaApi.on('select', onSelect);
    // Set initial selected index
    setSelectedIndex(emblaApi.selectedScrollSnap ? emblaApi.selectedScrollSnap() : 0);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);
  
  // Accordion states for Important Notes
  const [isAdditionalChargesOpen, setIsAdditionalChargesOpen] = useState(false);
  const [isSetupTimeOpen, setIsSetupTimeOpen] = useState(false);
  const [isRetainerOpen, setIsRetainerOpen] = useState(false);
  const [isFinalPaymentOpen, setIsFinalPaymentOpen] = useState(false);
  const [isCancellationsOpen, setIsCancellationsOpen] = useState(false);
  const [isReschedulingOpen, setIsReschedulingOpen] = useState(false);
  const [isContentStyleOpen, setIsContentStyleOpen] = useState(false);
  const [isLocationAccessOpen, setIsLocationAccessOpen] = useState(false);
  const [isTimeExtensionOpen, setIsTimeExtensionOpen] = useState(false);


  return (
    <div className='bg-[#f4f1e9] overflow-x-hidden'>
      {/* Hero Section */}
      <div className="relative h-[200px] md:h-[350px] overflow-x-hidden">
        <img
          src="assets/content-creator/hero-2-content-creator-wedding-DMV-date-saver-co.webp"
          alt="Content creator filming weddings for Date Saver Co"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-60 flex w-full">
          <h1 className="text-white font-the-seasons text-md md:text-4xl md:text-left text-center flex items-end pl-5 pb-16 md:items-center md:pl-16 md:pt-44">
            Event Content Creator – capturing your real with our reels
          </h1>
        </div>
      </div>

      {/* What's Included Section - Two Column Layout with Carousel */}
      <div className="py-16 px-4 md:py-20 md:px-16 bg-[#f4f1e9] overflow-x-hidden">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-[1fr,auto] gap-8 md:gap-0 items-center">
          {/* Left: Text Content */}
          <div className="flex flex-col items-center px-4 md:px-0 md:pr-8">
            <h3 className="text-3xl md:text-4xl font-bold font-the-seasons mb-6 text-center">
              What is an<br/>Event Content Creator?
            </h3>
            <p className="text-md leading-relaxed font-columbia-serial text-[#231f20] mb-6 text-justify max-w-lg">
              Our content creator uses an iPhone to capture all the fun, chaos, and in-betweens that usually go unseen, then turns them into short-form videos that let you relive the best moments as it happened, without adding another layer of artificiality.
            </p>
            <div className='flex flex-col items-center justify-center mb-6 md:mb-0'>
              <button onClick={scrollToPricing} className="font-the-seasons px-8 py-3 bg-gradient-to-r from-[#f4f1e9] to-[#e8e4d9] hover:from-[#e8e4d9] hover:to-[#dcd8c9] text-[#231f20] font-bold rounded-full border border-[#231f20]/20 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105">
                BOOK NOW
              </button>
              {/* Desktop only How It Works button below BOOK NOW */}
              <button
                onClick={scrollToHowItWorks}
                className="hidden md:inline-block font-the-seasons px-8 py-3 bg-gradient-to-r from-[#231f20] to-[#393532] hover:from-[#393532] hover:to-[#231f20] text-[#f4f1e9] font-bold rounded-full border border-[#231f20] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 mt-4"
                aria-label="Scroll to How it works"
              >
                How It Works
              </button>
            </div>
          </div>

          {/* Right: Embla Carousel for Videos (confined box) */}
          <div className="w-full flex justify-center items-center md:pl-4 relative">
            <div className="embla w-full max-w-[300px] md:max-w-[350px] aspect-[9/16] overflow-hidden rounded-md shadow-lg mx-auto">
              <div className="relative">
                <div className="embla__viewport w-full h-full" ref={node => { emblaRef(node); emblaViewportRef.current = node; }}>
                  <div className="embla__container flex w-full h-full">
                    {videoSources.map((src, idx) => (
                      <div className="embla__slide flex-shrink-0 w-full h-full" key={src}>
                        <div className="relative w-full h-full flex flex-col items-center justify-center">
                          <video
                            ref={el => { videoRefs.current[idx] = el; }}
                            playsInline
                            loop
                            className="w-full h-full object-cover rounded-md"
                            onTimeUpdate={() => handleTimeUpdate(idx)}
                            onLoadedMetadata={() => handleLoadedMetadata(idx)}
                            onClick={() => handlePlayPause(idx)}
                            tabIndex={0}
                            style={{ outline: 'none' }}
                            preload="metadata"
                          >
                            <source src={src} type="video/webm" />
                            Your browser does not support the video tag.
                          </video>
                          {/* Controls Overlay */}
                          <div className="absolute bottom-2 left-0 w-full flex flex-col items-center px-2">
                            {/* Progress Bar */}
                            <input
                              type="range"
                              min={0}
                              max={1}
                              step={0.01}
                              value={progress[idx] || 0}
                              onChange={e => handleSeek(idx, parseFloat(e.target.value))}
                              className="w-full accent-[#231f20] h-1 mb-1"
                            />
                            {/* Play/Pause Button */}
                            <button
                              onClick={e => { e.stopPropagation(); handlePlayPause(idx); }}
                              className="bg-[#231f20]/80 text-[#f4f1e9] rounded-full px-3 py-1 text-xs font-bold shadow"
                              aria-label={playingIndex === idx ? 'Pause video' : 'Play video'}
                            >
                              {playingIndex === idx ? 'Pause' : 'Play'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Chevron navigation overlays */}
                {videoSources.length > 1 && (
                  <>
                    <button
                      type="button"
                      className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 transition-all duration-300 hover:scale-150 active:scale-95"
                      onClick={() => emblaApi && emblaApi.scrollPrev()}
                      aria-label="Previous video"
                    >
                      <FiChevronLeft size={30} className="text-white" />
                    </button>
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 z-10 rounded-full p-2 transition-all duration-300 hover:scale-150 active:scale-95"
                      onClick={() => emblaApi && emblaApi.scrollNext()}
                      aria-label="Next video"
                    >
                      <FiChevronRight size={30} className="text-white" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Scroll Button (mobile only) */}
      <div className="flex justify-center md:hidden mt-">
        <button
          onClick={scrollToHowItWorks}
          className="font-the-seasons px-8 py-3 bg-[#231f20] hover:bg-[#393532] text-[#f4f1e9] font-bold rounded-full border border-[#231f20] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 text-lg md:text-xl"
          aria-label="Scroll to How it works"
        >
          How It Works
        </button>
      </div>

      {/* Pricing Section */}
      <div className="py-16 px-4 md:py-20 bg-[#f4f1e9]">
        <h2 className="text-3xl md:text-5xl text-[#231f20] font-the-seasons mb-4 text-center font-normal tracking-wide">
          EVENT CONTENT CREATION
        </h2>
        <div className="mb-12 px-4 md:px-0">
          <div className="mx-auto divide-y divide-[#e8e4d9] bg-[#f4f1e9] rounded-xl overflow-hidden border border-[#e8e4d9]" style={{maxWidth: '540px'}}>
            <div className="flex flex-row items-stretch gap-2 py-2 md:py-2 px-3 md:px-4">
              <div className="font-bold text-[11px] md:text-[12px] text-[#231f20] min-w-[90px] md:min-w-[110px] text-left flex items-center leading-tight md:leading-tight">
                Additional time<span className="hidden md:inline">:</span><span className="inline md:hidden">:</span>
              </div>
                <div className="text-xs md:text-[12px] text-[#231f20] opacity-80 flex-1 text-left px-2 md:px-2 flex items-center leading-tight md:leading-tight">1 hour • 2 hours</div>
            </div>
            <div className="flex flex-row items-stretch gap-2 py-2 md:py-2 px-3 md:px-4">
              <div className="font-bold text-[11px] md:text-[12px] text-[#231f20] min-w-[90px] md:min-w-[110px] text-left flex items-center leading-tight md:leading-tight">
                Additional videos<span className="hidden md:inline">:</span><span className="inline md:hidden">:</span>
              </div>
                <div className="text-xs md:text-[12px] text-[#231f20] opacity-80 flex-1 text-left px-2 md:px-2 flex items-center leading-tight md:leading-tight">30 second video • 60 second video</div>
            </div>
            <div className="flex flex-row items-stretch gap-2 py-2 md:py-2 px-3 md:px-4">
              <div className="font-bold text-[11px] md:text-[12px] text-[#231f20] min-w-[90px] md:min-w-[110px] text-left flex items-center leading-tight md:leading-tight">
                Delivery<span className="hidden md:inline">:</span><span className="inline md:hidden">:</span>
              </div>
                <div className="text-xs md:text-[12px] text-[#231f20] opacity-80 flex-1 text-left px-2 md:px-2 flex items-center leading-tight md:leading-tight">Clips sent within 48 hours • Edited videos done within 2 weeks</div>
            </div>
            <div className="flex flex-row items-stretch gap-2 py-2 md:py-2 px-3 md:px-4">
              <div className="font-bold text-[11px] md:text-[12px] text-[#231f20] min-w-[90px] md:min-w-[110px] text-left flex items-center leading-tight md:leading-tight">
                Device Used<span className="hidden md:inline">:</span><span className="inline md:hidden">:</span>
              </div>
                <div className="text-xs md:text-[12px] text-[#231f20] opacity-80 flex-1 text-left px-2 md:px-2 flex items-center leading-tight md:leading-tight">iPhone 16 Pro Max (native camera app for user friendly footage)</div>
            </div>
          </div>
        </div>

  <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch">
          {/* Package 1 */}
          <div className="bg-[#dfded9] border border-[#231f20] rounded-[28px] p-6 md:p-8 flex flex-col text-[#231f20] max-w-[320px] mx-auto w-full">
              <div className="text-center border-b border-[#231f20] pb-6 mb-6">
                <h3 className="text-2xl md:text-3xl font-the-seasons font-bold tracking-widest">MINI</h3>
              </div>
              <div className="text-center mb-6">
                <span className="text-3xl md:text-4xl font-bold">3 HOUR</span>
              </div>
            <ul className="space-y-1 mb-4 flex-grow text-sm">
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>3 hour coverage</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>200 clips</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>1 edited video</span>
              </li>
              <li className="flex items-start ml-4 italic">
                <span>60-90 seconds</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✗</span>
                <span>option to split into shorter videos</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Dropbox & hard drive storage | year</span>
              </li>
            </ul>
            <a href="https://bookdatesaverco.as.me/?appointmentType=84751325" target="_blank" rel="noopener noreferrer" className="font-the-seasons mx-auto px-12 py-3 md:px-14 md:py-4 bg-gradient-to-r from-[#f4f1e9] to-[#e8e4d9] hover:from-[#4a4540] hover:to-[#231f20] text-[#231f20] hover:text-[#f4f1e9] font-bold border border-[#231f20]/20 hover:border-[#231f20] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 rounded-full tracking-wider text-center inline-block text-lg md:text-xl">
              INQUIRE
            </a>
          </div>

          {/* Package 2 */}
          <div className="bg-[#dfded9] border border-[#231f20] rounded-[28px] p-6 md:p-8 flex flex-col text-[#231f20] max-w-[320px] mx-auto w-full">
              <div className="text-center border-b border-[#231f20] pb-6 mb-6">
                <h3 className="text-2xl md:text-3xl font-the-seasons font-bold tracking-widest">FULL</h3>
              </div>
              <div className="text-center mb-6">
                <span className="text-3xl md:text-4xl font-bold">4 HOUR</span>
              </div>
            <ul className="space-y-1 mb-4 flex-grow text-sm">
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>4 hour coverage</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>300 clips</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>2 edited videos</span>
              </li>
              <li className="flex items-start ml-4 italic">
                <span>60-90 seconds; 30-45 seconds</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>option to split into shorter videos</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Dropbox & hard drive storage | year</span>
              </li>
            </ul>
            <a href="https://bookdatesaverco.as.me/?appointmentType=84751364" target="_blank" rel="noopener noreferrer" className="font-the-seasons mx-auto px-12 py-3 md:px-14 md:py-4 bg-gradient-to-r from-[#f4f1e9] to-[#e8e4d9] hover:from-[#4a4540] hover:to-[#231f20] text-[#231f20] hover:text-[#f4f1e9] font-bold border border-[#231f20]/20 hover:border-[#231f20] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 rounded-full tracking-wider text-center inline-block text-lg md:text-xl">
              INQUIRE
            </a>
          </div>

          {/* Package 3 - Most Popular */}
          <div className="bg-[#231f20] rounded-[28px] p-6 md:p-8 flex flex-col text-[#f4f1e9] relative max-w-[320px] mx-auto w-full overflow-visible">
            {/* 3D Bookmark Ribbon */}
            <div className="absolute -top-2 right-8 w-12 h-28 z-20">
              <div className="relative w-full h-full overflow-hidden"
                   style={{
                     clipPath: 'polygon(0 8%, 5% 3%, 10% 1%, 20% 0, 80% 0, 90% 1%, 95% 3%, 100% 8%, 100% 100%, 50% 85%, 0 100%)',
                   }}>
                {/* Main ribbon body with smooth gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 shadow-lg">
                </div>
                {/* Shine effect overlay - more subtle and blended */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-amber-200/20 to-transparent opacity-60">
                </div>
                {/* Deeper shine accent on top-left - smoother transition */}
                <div className="absolute top-0 left-0 w-10 h-20 bg-gradient-to-br from-white/50 via-amber-100/30 to-transparent opacity-70">
                </div>
                {/* Left edge shadow for 3D depth - smoother gradient */}
                <div className="absolute left-0 top-0 w-2 h-full bg-gradient-to-r from-amber-900/50 via-amber-700/30 to-transparent">
                </div>
                {/* Right edge highlight for 3D depth - smoother gradient */}
                <div className="absolute right-0 top-0 w-2 h-full bg-gradient-to-l from-amber-200/50 via-amber-300/30 to-transparent">
                </div>
                {/* Inner shadow on the inverted V-notch - left side with smooth blend */}
                <div className="absolute bottom-0 left-0 w-1/2 h-8 bg-gradient-to-br from-amber-800/40 via-amber-600/20 to-transparent"
                     style={{
                       clipPath: 'polygon(0 100%, 100% 0%, 100% 100%)',
                     }}>
                </div>
                {/* Inner shadow on the inverted V-notch - right side with smooth blend */}
                <div className="absolute bottom-0 right-0 w-1/2 h-8 bg-gradient-to-bl from-amber-800/40 via-amber-600/20 to-transparent"
                     style={{
                       clipPath: 'polygon(0 0%, 100% 100%, 0 100%)',
                     }}>
                </div>
              </div>
            </div>
            
            {/* Most Popular - Desktop */}
            <div className="hidden md:block absolute -top-11 left-0 md:text-6xl text-lg text-[#df5745]" style={{ fontFamily: "'Beautifuly Delicious', cursive" }}>
              Most Popular
            </div>
            {/* Most Popular - Mobile Badge */}
            <div className="md:hidden text-center mb-3">
              <span className="inline-block text-3xl text-[#df5745]" style={{ fontFamily: "'Beautifuly Delicious', cursive" }}>
                Most Popular
              </span>
            </div>
            <div className="text-center border-b border-[#f4f1e9] pb-6 mb-6">
              <h3 className="text-2xl md:text-3xl font-the-seasons font-bold tracking-widest">PREMIUM</h3>
            </div>
            <div className="text-center mb-6">
              <span className="text-3xl md:text-4xl font-bold">6 HOUR</span>
            </div>
            <ul className="space-y-1 mb-4 flex-grow text-sm">
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>6 hour coverage</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>400 clips</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>3 edited videos</span>
              </li>
              <li className="flex items-start ml-4 italic">
                <span>60-90 seconds; 30-45 seconds (x2)</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>option to split into shorter videos</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Dropbox & hard drive storage | year</span>
              </li>
            </ul>
            <a href="https://bookdatesaverco.as.me/?appointmentType=84751382" target="_blank" rel="noopener noreferrer" className="font-the-seasons mx-auto px-12 py-3 md:px-14 md:py-4 bg-gradient-to-r from-[#f4f1e9] to-[#e8e4d9] hover:from-amber-400 hover:to-amber-500 text-[#231f20] font-bold border border-[#231f20]/20 hover:border-amber-600/30 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 rounded-full tracking-wider text-center inline-block text-lg md:text-xl">
              INQUIRE
            </a>
          </div>
        </div>

        {/* Inquiry Box */}
  <div id="not-sure-box" className="max-w-xl mx-auto mt-16 bg-[#231f20] rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold font-the-seasons mb-4">NOT SURE?</h3>
          <p className="text-sm mb-6">Tell us more about your event. Let's talk over call/email. From there, we'll match you and send a quote by email.</p>
          <a href="https://bookdatesaverco.as.me/EVENTCONTENTCREATION" target="_blank" rel="noopener noreferrer" className="inline-block bg-[#f4f1e9] text-[#231f20] px-8 py-3 rounded-full font-the-seasons font-semibold hover:bg-white transition duration-300">
            TALK TO US
          </a>
        </div>

        {/* Reviews Grid */}
        <ReviewsGrid />
      </div>

  {/* How it works Section */}
  <div id="how-it-works" className="py-16 px-4 md:py-20 bg-[#231f20]">
        <h2 className="text-3xl md:text-5xl text-[#f4f1e9] font-the-seasons mb-12 md:mb-16 text-center font-normal">
          How it works
        </h2>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {/* Step 1 - Inquire */}
          <div className='bg-transparent border-2 border-[#f4f1e9] rounded-3xl p-8 text-[#f4f1e9]'>
            <h3 className="text-xl md:text-2xl font-bold font-the-seasons mb-6 text-center">
              1 <span className='font-sans font-extralight'>•</span> Inquire
            </h3>
            <p className="text-sm md:text-base font-columbia-serial leading-relaxed text-justify mb-4">
              Choose a package that fits your needs and submit the inquiry.
            </p>
            <p className="text-sm md:text-base font-columbia-serial leading-relaxed text-justify">
              <span className='font-bold'>Not sure what fits?</span>
              <br/>
              Choose the <a
                href="#not-sure-box"
                className="underline cursor-pointer text-[#231f20] hover:text-[#df5745] transition-colors duration-200"
                onClick={e => {
                  e.preventDefault();
                  const box = document.getElementById('not-sure-box');
                  if (!box) return;
                  const yOffset = -24;
                  const targetY = box.getBoundingClientRect().top + window.pageYOffset + yOffset;
                  const startY = window.scrollY;
                  const distance = targetY - startY;
                  const duration = 900;
                  let startTime: number | null = null;
                  function easeInOutQuad(t: number) {
                    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
                  }
                  function animateScroll(currentTime: number) {
                    if (startTime === null) startTime = currentTime;
                    const timeElapsed = currentTime - (startTime as number);
                    const progress = Math.min(timeElapsed / duration, 1);
                    const ease = easeInOutQuad(progress);
                    window.scrollTo(0, startY + distance * ease);
                    if (progress < 1) {
                      window.requestAnimationFrame(animateScroll);
                    }
                  }
                  window.requestAnimationFrame(animateScroll);
                }}
              >TALK TO US</a> option and we will get back to you via email.
            </p>
          </div>

          {/* Step 2 - Call */}
          <div className='bg-transparent border-2 border-[#f4f1e9] rounded-3xl p-8 text-[#f4f1e9]'>
            <h3 className="text-xl md:text-2xl font-bold font-the-seasons mb-6 text-center">
              2 <span className='font-sans font-extralight'>•</span> Call
            </h3>
            <p className="text-sm md:text-base font-columbia-serial leading-relaxed text-justify mb-4">
              Join a brief <span className='font-bold'>consult call</span> to go over your event details.
            </p>
            <p className="text-sm md:text-base font-columbia-serial leading-relaxed text-justify">
              After the call, review and complete the <span className='font-bold italic'>Client Agreement Form and Retainer Invoice</span> to officially book your appointment.
            </p>
          </div>

          {/* Step 3 - Date Saved */}
          <div className='bg-transparent border-2 border-[#f4f1e9] rounded-3xl p-8 text-[#f4f1e9]'>
            <h3 className="text-xl md:text-2xl font-bold font-the-seasons mb-6 text-center">
              3 <span className='font-sans font-extralight'>•</span> Date Saved
            </h3>
            <p className="text-sm md:text-base font-columbia-serial leading-relaxed text-justify mb-4">
              Your remaining balance and any add-ons will be auto-charged 48 hours before your event.
            </p>
            <p className="text-sm md:text-base font-columbia-serial leading-relaxed text-justify">
              And now you can unplug and enjoy your event as it is. <span className='font-bold'>WITHOUT</span> phones and without a worry.
            </p>
          </div>
        </div>
      </div>

      {/* Important Notes Section */}
      <div className="py-16 px-4 md:py-20 bg-[#f4f1e9] overflow-x-hidden">
        <AnimatedSection>
        <h2 className="text-3xl md:text-5xl text-[#231f20] font-the-seasons mb-12 md:mb-16 text-center font-normal">
          Important Notes
        </h2>
        </AnimatedSection>
        
        <div className="max-w-3xl mx-auto space-y-4">
          {/* Additional Charges Accordion */}
          <AnimatedSection>
          <div className="border-b border-[#231f20]/20 pb-4">
            <button 
              onClick={() => setIsAdditionalChargesOpen(!isAdditionalChargesOpen)} 
              className="w-full text-left flex justify-between items-center gap-4 px-2 min-w-0"
            >
              <h3 className="text-base md:text-lg font-bold text-[#231f20] break-words flex-1">Add-ons After Booking</h3>
              <svg 
                className={`w-5 h-5 transition-transform duration-500 ease-in-out flex-shrink-0 text-[#231f20] ${isAdditionalChargesOpen ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div 
              className={`overflow-hidden transition-all duration-500 ease-in-out ${
                isAdditionalChargesOpen ? 'max-h-[500px] opacity-100 mt-3' : 'max-h-0 opacity-0'
              }`}
            >
              <p className="text-sm text-[#231f20] font-columbia-serial px-2 text-justify">
                Any add-ons or customizations requested after booking will be billed separately or added onto the final/remaining payment balance. These charges will be processed 48 hours before the scheduled event date.
              </p>
            </div>
          </div>
          </AnimatedSection>

          {/* Set-up Time Accordion */}
          <AnimatedSection>
          <div className="border-b border-[#231f20]/20 pb-4">
            <button 
              onClick={() => setIsSetupTimeOpen(!isSetupTimeOpen)} 
              className="w-full text-left flex justify-between items-center gap-4 px-2 min-w-0"
            >
              <h3 className="text-base md:text-lg font-bold text-[#231f20] break-words flex-1">Set-up Time</h3>
              <svg 
                className={`w-5 h-5 transition-transform duration-500 ease-in-out flex-shrink-0 text-[#231f20] ${isSetupTimeOpen ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div 
              className={`overflow-hidden transition-all duration-500 ease-in-out ${
                isSetupTimeOpen ? 'max-h-[500px] opacity-100 mt-3' : 'max-h-0 opacity-0'
              }`}
            >
              <p className="text-sm text-[#231f20] font-columbia-serial px-2 text-justify">
                Please allow our content creator 30-60 min to set up and get familiar with the environment before the event.
              </p>
            </div>
          </div>
          </AnimatedSection>

          {/* Retainer Investment Accordion */}
          <AnimatedSection>
          <div className="border-b border-[#231f20]/20 pb-4">
            <button 
              onClick={() => setIsRetainerOpen(!isRetainerOpen)} 
              className="w-full text-left flex justify-between items-center gap-4 px-2 min-w-0"
            >
              <h3 className="text-base md:text-lg font-bold text-[#231f20] break-words flex-1">Retainer Investment</h3>
              <svg 
                className={`w-5 h-5 transition-transform duration-500 ease-in-out flex-shrink-0 text-[#231f20] ${isRetainerOpen ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div 
              className={`overflow-hidden transition-all duration-500 ease-in-out ${
                isRetainerOpen ? 'max-h-[500px] opacity-100 mt-3' : 'max-h-0 opacity-0'
              }`}
            >
              <p className="text-sm text-[#231f20] font-columbia-serial px-2 text-justify mb-3">
                A non-refundable retainer investment of 50% of the total cost is required at the time of booking to secure your event date.
              </p>
              <p className="text-sm text-[#231f20] font-columbia-serial px-2 text-justify italic">
                Why a Retainer Investment? It helps us reserve your date, start planning your event, and cover initial costs. It's deducted from your final payment and ensures we can provide the best service for your event. Thank you for your understanding.
              </p>
            </div>
          </div>
          </AnimatedSection>

          {/* Final Balance Accordion */}
          <AnimatedSection>
          <div className="border-b border-[#231f20]/20 pb-4">
            <button 
              onClick={() => setIsFinalPaymentOpen(!isFinalPaymentOpen)} 
              className="w-full text-left flex justify-between items-center gap-4 px-2 min-w-0"
            >
              <h3 className="text-base md:text-lg font-bold text-[#231f20] break-words flex-1">Final Balance</h3>
              <svg 
                className={`w-5 h-5 transition-transform duration-500 ease-in-out flex-shrink-0 text-[#231f20] ${isFinalPaymentOpen ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div 
              className={`overflow-hidden transition-all duration-500 ease-in-out ${
                isFinalPaymentOpen ? 'max-h-[500px] opacity-100 mt-3' : 'max-h-0 opacity-0'
              }`}
            >
              <p className="text-sm text-[#231f20] font-columbia-serial px-2 text-justify">
                The remaining 50% balance will be automatically charged to the same payment method 48 hours before your event. If there's an issue processing the payment, we'll reach out to help resolve it. However, unresolved payment issues may result in cancellation of the booking.
              </p>
            </div>
          </div>
          </AnimatedSection>

          {/* Cancellations Accordion */}
          <AnimatedSection>
          <div className="border-b border-[#231f20]/20 pb-4">
            <button 
              onClick={() => setIsCancellationsOpen(!isCancellationsOpen)} 
              className="w-full text-left flex justify-between items-center gap-4 px-2 min-w-0"
            >
              <h3 className="text-base md:text-lg font-bold text-[#231f20] break-words flex-1">Cancellations</h3>
              <svg 
                className={`w-5 h-5 transition-transform duration-500 ease-in-out flex-shrink-0 text-[#231f20] ${isCancellationsOpen ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div 
              className={`overflow-hidden transition-all duration-500 ease-in-out ${
                isCancellationsOpen ? 'max-h-[500px] opacity-100 mt-3' : 'max-h-0 opacity-0'
              }`}
            >
              <p className="text-sm text-[#231f20] font-columbia-serial px-2 text-justify">
                Bookings made or canceled less than 48 hours before the event are non-refundable.
              </p>
            </div>
          </div>
          </AnimatedSection>

          {/* Rescheduling Accordion */}
          <AnimatedSection>
          <div className="border-b border-[#231f20]/20 pb-4">
            <button 
              onClick={() => setIsReschedulingOpen(!isReschedulingOpen)} 
              className="w-full text-left flex justify-between items-center gap-4 px-2 min-w-0"
            >
              <h3 className="text-base md:text-lg font-bold text-[#231f20] break-words flex-1">Rescheduling</h3>
              <svg 
                className={`w-5 h-5 transition-transform duration-500 ease-in-out flex-shrink-0 text-[#231f20] ${isReschedulingOpen ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div 
              className={`overflow-hidden transition-all duration-500 ease-in-out ${
                isReschedulingOpen ? 'max-h-[500px] opacity-100 mt-3' : 'max-h-0 opacity-0'
              }`}
            >
              <p className="text-sm text-[#231f20] font-columbia-serial px-2 text-justify">
                Unexpected things happen! Let us know if you need to reschedule, and we'll work with you to find a new time, subject to availability.
              </p>
            </div>
          </div>
          </AnimatedSection>

          {/* Content Style & Format Disclaimer Accordion */}
          <AnimatedSection>
          <div className="border-b border-[#231f20]/20 pb-4">
            <button 
              onClick={() => setIsContentStyleOpen(!isContentStyleOpen)} 
              className="w-full text-left flex justify-between items-center gap-4 px-2 min-w-0"
            >
              <h3 className="text-base md:text-lg font-bold text-[#231f20] break-words flex-1">Content Style & Format Disclaimer</h3>
              <svg 
                className={`w-5 h-5 transition-transform duration-500 ease-in-out flex-shrink-0 text-[#231f20] ${isContentStyleOpen ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div 
              className={`overflow-hidden transition-all duration-500 ease-in-out ${
                isContentStyleOpen ? 'max-h-[500px] opacity-100 mt-3' : 'max-h-0 opacity-0'
              }`}
            >
              <p className="text-sm text-[#231f20] font-columbia-serial px-2 text-justify">
                All footage will be recorded vertically (9:16 format) and is intended for short-term content ranging from 15 to 120 second edits, not full-length cinematic films. Deliverables are provided as clips and edited reels through Dropbox.
              </p>
            </div>
          </div>
          </AnimatedSection>

          {/* Location Access Accordion */}
          <AnimatedSection>
          <div className="border-b border-[#231f20]/20 pb-4">
            <button 
              onClick={() => setIsLocationAccessOpen(!isLocationAccessOpen)} 
              className="w-full text-left flex justify-between items-center gap-4 px-2 min-w-0"
            >
              <h3 className="text-base md:text-lg font-bold text-[#231f20] break-words flex-1">Location Access</h3>
              <svg 
                className={`w-5 h-5 transition-transform duration-500 ease-in-out flex-shrink-0 text-[#231f20] ${isLocationAccessOpen ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div 
              className={`overflow-hidden transition-all duration-500 ease-in-out ${
                isLocationAccessOpen ? 'max-h-[500px] opacity-100 mt-3' : 'max-h-0 opacity-0'
              }`}
            >
              <p className="text-sm text-[#231f20] font-columbia-serial px-2 text-justify">
                It is the client's responsibility to ensure that <span className="font-bold">private</span> locations are accessible at the agreed-upon times.
              </p>
            </div>
          </div>
          </AnimatedSection>

          {/* Last Minute Time Extension Accordion */}
          <AnimatedSection>
          <div className="border-b border-[#231f20]/20 pb-4">
            <button 
              onClick={() => setIsTimeExtensionOpen(!isTimeExtensionOpen)} 
              className="w-full text-left flex justify-between items-center gap-4 px-2 min-w-0"
            >
              <h3 className="text-base md:text-lg font-bold text-[#231f20] break-words flex-1">Last Minute Time Extension</h3>
              <svg 
                className={`w-5 h-5 transition-transform duration-500 ease-in-out flex-shrink-0 text-[#231f20] ${isTimeExtensionOpen ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div 
              className={`overflow-hidden transition-all duration-500 ease-in-out ${
                isTimeExtensionOpen ? 'max-h-[500px] opacity-100 mt-3' : 'max-h-0 opacity-0'
              }`}
            >
              <p className="text-sm text-[#231f20] font-columbia-serial px-2 text-justify">
                If there are delays, and you would wish to extend your time, you may do so at the event (just give us a call or approach any of our team members). Appointment time extensions are subject to availability and charges specific to the service.
              </p>
            </div>
          </div>
          </AnimatedSection>
        </div>
      </div>
    </div>
  );
};

export default ContentCreator;
